import {
  MentionNode,
  $createMentionNode,
} from '@/components/editor/MentionPlugin/MentionNode';
import { INSERT_USER_COMMAND } from '@/components/editor/MentionPlugin/MentionPlugin';
import TypeaheadPlugin from '@/components/editor/TypeaheadPlugin';
import { useUserMap } from '@/features/users/users.hooks';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
} from 'lexical';
import { useEffect } from 'react';
import { User } from '@certhon/models';

export function MentionTypeaheadPlugin() {
  const [editor] = useLexicalComposerContext();
  const users = useUserMap('loginname');

  useEffect(() => {
    if (!editor.hasNodes([MentionNode])) {
      throw new Error(
        'MentionTypeaheadPlugin: MentionNode not registered on editor',
      );
    }

    return mergeRegister(
      editor.registerCommand<User>(
        INSERT_USER_COMMAND,
        (user) => {
          const mentionNode = $createMentionNode(user.loginname);
          $insertNodes([mentionNode]);
          if ($isRootOrShadowRoot(mentionNode.getParentOrThrow())) {
            $wrapNodeInElement(mentionNode, $createParagraphNode).selectEnd();
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  const usersList = Array.from(users.values());

  // Filter to show only active, internal users in autocomplete
  const filteredUsersList = usersList.filter(
    (user) => user.active && user.internal,
  );

  const options = filteredUsersList.map((u) => {
    const option = new MenuOption(`mention-${u.recnum}`);
    return Object.assign(option, {
      value: u.recnum,
      keywords: [u.loginname, u.name],
      loginname: u.loginname,
      onSelect: () => {
        editor.dispatchCommand(INSERT_USER_COMMAND, u);
      },
    });
  });

  return (
    <TypeaheadPlugin
      options={options}
      renderItem={(u) => (
        <span className="rounded bg-blue-50 px-1 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
          @{u.loginname}
        </span>
      )}
      selectItem={(u) => {
        u.onSelect?.();
      }}
      trigger="@"
    />
  );
}
